const toastr = require('toastr');

/**
 * ToastFactory produces notifications that pop up in the corner of the screen ("toasts").
 * It uses this package: https://github.com/CodeSeven/toastr
 *
 * @constructor
 *
 * @param $rootScope
 * @param {LangService} langService
 */
const ToastFactory = function (
    $rootScope,
    langService
) {
    let toastsToClearOnNavigation = [];

    // Set some default options for the toast library...
    // @see https://github.com/CodeSeven/toastr

    // How long the toast will display without user interaction (ms)
    toastr.options.timeOut = 3000;

    // The toast will remain visible for as long as the mouse is hovering over it.
    // It will then disappear after this number of ms after the mouse leaves it.
    toastr.options.extendedTimeOut = 5000;

    toastr.options.showDuration = 100; // How long the entry animation lasts (ms)
    toastr.options.showEasing = 'linear';

    toastr.options.hideDuration = 100; // How long the exit animation lasts (ms)
    toastr.options.hideEasing = 'linear';

    // Display toasts in bottom right corner
    toastr.options.positionClass = 'toast-bottom-right';

    // Add new toasts to bottom and push up
    toastr.options.newestOnTop = false;

    // Prevent multiple of the same toast showing.
    toastr.options.preventDuplicates = true;

    const getLangString = (str) => langService.getText(str);

    /**
     * Display a toast.
     *
     * Title should be the name of a language key.
     * Text should be a raw HTML string. Or it can be an object with the keys "key" and "params".
     * The difference is because the title will generally be hard coded ("Deleting Address") and the text will
     * generally be variable ("123 Fake St.")
     *
     * @param {string} style
     * @return {function(string, ?string|object|LangString, ?object)}
     */
    const createToast = (style) => (title, text, options) => {
        if (title) {
            title = getLangString(title);
        }

        if (!options) {
            options = {};
        }

        // Creating the toast removes additional options, so grab this value before it is lost.
        let clearOnNavigation = !!options.clearOnNavigation;

        if (clearOnNavigation) {
            options.onHidden = function () {
                // Remove from the array of toastsToClearOnNavigation when it's hidden.
                const thisToast = this;
                toastsToClearOnNavigation = toastsToClearOnNavigation.filter((t) => t !== thisToast);
            };
        }

        // This can be a plain object or a LangString instance.
        if (text && typeof text === 'object' && text.hasOwnProperty('key')) {
            text = langService.getText(text.key, text.params);
        }

        const toast = toastr[style](text, title, options);

        if (clearOnNavigation) {
            toastsToClearOnNavigation.push(toast);
        }

        return toast;
    };

    /**
     * @type {function(string, ?string=, ?Object=)}
     */
    this.info = createToast('info');

    /**
     * @type {function(string, ?string=, ?Object=)}
     */
    this.success = createToast('success');

    /**
     * @type {function(string, ?string=, ?Object=)}
     */
    this.warning = createToast('warning');

    this.clear = toastr.clear;

    /**
     * Display an error toast.
     *
     * This will not automatically clear. The user must click on it to dismiss.
     * An OK button will be added automatically.
     *
     * @param {string|null} title
     * @param {string} [text]
     * @param {object} [options]
     *
     * @return {object}
     */
    this.error = (title, text, options) => {
        if (!options) {
            options = {};
        }

        // Keep it shown until clicked
        options.timeOut = 0;
        options.extendedTimeOut = 0;
        options.tapToDismiss = true;

        text = '<button class="btn btn-default btn-sm error-toast-btn">'
            + (options.buttonText || getLangString('OK'))
            + '</button>'
            + (text || '');

        return createToast('error')(title, text, options);
    };

    /**
     * Display a toast with a spinning icon.
     *
     * It will show until it is cleared. Run this.clear() with the return value of this function to clear it.
     * You can provide a promise and the toast will automatically clear after the promise resolves or rejects.
     *
     * @param {string} title
     * @param {string} [text]
     * @param {Promise} [watchPromise] Automatically clear when this promise resolves.
     * @param {object} [options]
     *
     * @return {object}
     */
    this.spinner = (title, text, watchPromise, options) => {
        if (!options) {
            options = {};
        }
        // Keep it shown
        options.timeOut = 0;
        options.extendedTimeOut = 0;
        // Allow tapping to dismiss in case it gets stuck or is in the way.
        options.tapToDismiss = true;

        options.iconClass = 'toast-spinner';

        let toast = createToast('info')(title, text, options);

        if (watchPromise) {
            const clearSpinner = () => {
                // This needs to wait a short time before clearing because it might resolve/reject so
                // fast the spinner hasn't been shown yet.
                setTimeout(() => this.clear(toast), 101);
            };

            // Remove the spinner whether the promise resolves or rejects.
            // We could use .finally, but then we'd need a .catch anyway, so this seems clearer.
            // See https://digitickets.atlassian.net/browse/PROP-2885?focusedCommentId=159226
            watchPromise
                .then(clearSpinner)
                .catch(clearSpinner);
        }

        return toast;
    };

    /**
     * Display a toast at the top of the screen.
     * This replaces what angular-ui's notifications were previously used for.
     *
     * @param {string} style
     * @return {function(*=, *=, *=): *}
     */
    const createTopToast = (style) => (title, text, options) => {
        if (!options) {
            options = {};
        }
        options.positionClass = 'toast-top-center';

        let toast = createToast(style);

        return toast(title, text, options);
    };

    /** {function(string, ?string, ?object)} */
    this.infoTop = createTopToast('info');

    /** {function(string, ?string, ?object)} */
    this.successTop = createTopToast('success');

    /** {function(string, ?string, ?object)} */
    this.warningTop = createTopToast('warning');

    /**
     * Display a big error at the top of the screen.
     * This can only be dismissed with an OK button, which will be added.
     *
     * @param {string|null} title
     * @param {string} [text]
     * @param {{}|null} [options]
     *
     * @returns {*}
     */
    this.errorTop = (title, text, options = null) => {
        if (!options) {
            options = {};
        }

        // Keep it shown until clicked
        options.timeOut = 0;
        options.extendedTimeOut = 0;
        options.tapToDismiss = false;

        // Add a close (OK) button
        options.closeButton = true;
        options.closeHtml = '<button class="btn btn-default btn-lg error-toast-btn">'
            + getLangString('OK')
            + '</button>';

        return createTopToast('error')(title, text, options);
    };

    /**
     * @param {jQuery} $toastElement
     * @param {string} text
     */
    this.updateText = ($toastElement, text) => {
        let $messageEl = $toastElement.find('.toast-message');
        if ($messageEl.length > 0) {
            $messageEl.text(text);
        } else {
            $toastElement.append($('<div class="toast-message" />').text(text));
        }
    };

    this.clearToastsOnNavigation = () => {
        // Clear any toasts that are set to clear on navigation.
        while (toastsToClearOnNavigation.length > 0) {
            const toast = toastsToClearOnNavigation.pop();
            this.clear(toast);
        }
    };

    $rootScope.$on('$locationChangeStart', () => {
        this.clearToastsOnNavigation();
    });
};

module.exports = ToastFactory;
